import { useMemo } from 'react';

import { AuthProvider, type UserSession } from '@/api/client';

import { useAuth } from './use-auth';

/**
 * Sentinel for "no SSO" — a local account with a null `auth_provider`. Tenant config
 * lists it alongside the real providers so "SSO only" is expressible.
 */
export const NO_AUTH_PROVIDER = 'none';

/** A value a tenant's `auth_providers` rule may list. */
export type TenantAuthProviderOption = AuthProvider | typeof NO_AUTH_PROVIDER;

/**
 * One `auth_providers` rule from tenant config. A rule with no domains is the wildcard:
 * it applies to any domain no other rule claims.
 */
interface AuthProviderRule {
  emailDomains: string[];
  providers: TenantAuthProviderOption[];
}

const KNOWN_PROVIDERS = new Set<string>([
  ...Object.values(AuthProvider),
  NO_AUTH_PROVIDER,
]);

/** The lowercased domain of an email address, or null if there isn't one. */
export function emailDomainOf(email: string | null | undefined): string | null {
  if (!email || !email.includes('@')) return null;
  return (
    email
      .slice(email.lastIndexOf('@') + 1)
      .trim()
      .toLowerCase() || null
  );
}

/**
 * Reads the rules the session carries for the tenant in view. `website_config` is an
 * untyped bag, so anything malformed is dropped rather than trusted.
 */
function parseRules(user: UserSession | null | undefined): AuthProviderRule[] {
  const raw = (user?.website_config as Record<string, unknown> | undefined)
    ?.auth_providers;
  if (!Array.isArray(raw)) return [];

  const rules: AuthProviderRule[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const { email_domains: rawDomains, providers: rawProviders } = entry as {
      email_domains?: unknown;
      providers?: unknown;
    };

    const providers = (Array.isArray(rawProviders) ? rawProviders : []).filter(
      (provider): provider is TenantAuthProviderOption =>
        typeof provider === 'string' && KNOWN_PROVIDERS.has(provider)
    );
    // The backend rejects an empty rule; if one reaches us anyway, ignoring it leaves the
    // dropdown unrestricted rather than unusable.
    if (providers.length === 0) continue;

    const emailDomains = (Array.isArray(rawDomains) ? rawDomains : [])
      .filter((domain): domain is string => typeof domain === 'string')
      .map((domain) => domain.trim().toLowerCase())
      .filter(Boolean);

    rules.push({ emailDomains, providers });
  }
  return rules;
}

/**
 * The auth providers assignable to a user at this email, or `null` when the tenant places
 * no restriction. Mirrors `SaasTenantConfig.allowed_auth_providers_for_email`: a rule
 * naming the address's domain wins, else the wildcard rule, else no restriction.
 *
 * This drives a UI affordance only — a user is not bound to a single tenant, so the API
 * has no tenant to enforce these rules against.
 */
export function getAllowedAuthProviders(
  user: UserSession | null | undefined,
  email: string | null | undefined
): TenantAuthProviderOption[] | null {
  const rules = parseRules(user);
  if (rules.length === 0) return null;

  const domain = emailDomainOf(email);
  const matched =
    (domain
      ? rules.find((rule) => rule.emailDomains.includes(domain))
      : undefined) ?? rules.find((rule) => rule.emailDomains.length === 0);

  return matched ? matched.providers : null;
}

/**
 * Hook form of {@link getAllowedAuthProviders}, resolved against the tenant currently in
 * view. Returns `null` when every provider is allowed.
 */
export function useAllowedAuthProviders(
  email: string | null | undefined
): TenantAuthProviderOption[] | null {
  const { user } = useAuth();
  return useMemo(() => getAllowedAuthProviders(user, email), [user, email]);
}

/** Every selectable value, in the order the dropdown shows them. */
export const ALL_AUTH_PROVIDER_OPTIONS: TenantAuthProviderOption[] = [
  NO_AUTH_PROVIDER,
  ...Object.values(AuthProvider),
];

/**
 * The values the dropdown should offer.
 *
 * `allowedProviders` is the tenant's resolved set (`null` for unrestricted). When
 * `grandfatherCurrent` is set — editing an existing user — a `currentProvider` the tenant
 * no longer allows is kept in the list, so opening the dialog shows what is actually
 * stored instead of silently presenting a different provider.
 */
export function getAuthProviderChoices(
  allowedProviders: TenantAuthProviderOption[] | null,
  currentProvider: TenantAuthProviderOption,
  { grandfatherCurrent }: { grandfatherCurrent: boolean }
): TenantAuthProviderOption[] {
  if (!allowedProviders) return ALL_AUTH_PROVIDER_OPTIONS;

  const allowed = ALL_AUTH_PROVIDER_OPTIONS.filter((provider) =>
    allowedProviders.includes(provider)
  );

  return grandfatherCurrent && !allowed.includes(currentProvider)
    ? [...allowed, currentProvider]
    : allowed;
}

/**
 * The provider a new user's field should snap to, or `null` when the current value is
 * already fine. A new user defaults to "None"; if the address typed in forbids that, the
 * field moves to the first provider the tenant does allow rather than carrying a value
 * the tenant disallows into the create call.
 */
export function getAuthProviderCorrection(
  allowedProviders: TenantAuthProviderOption[] | null,
  currentProvider: TenantAuthProviderOption
): TenantAuthProviderOption | null {
  if (!allowedProviders?.length) return null;
  if (allowedProviders.includes(currentProvider)) return null;
  return allowedProviders[0];
}
